import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type PropsWithChildren,
  type ReactElement,
} from 'react';

import type { ApiClient, ApiFailure } from '../api/client.ts';
import { sameOriginImageUploader, type ImageUploader } from '../content/upload.ts';
import { useSession, type SessionApi } from './useSession.ts';

export interface SessionContextValue extends SessionApi {
  readonly client: ApiClient;
  readonly imageUploader: ImageUploader;
  /** Re-check the server when an authenticated content request reports that the session is gone. */
  readonly handleFailure: (failure: ApiFailure) => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/** Owns the app's one useSession invocation and does not persist user or CSRF state. */
export const SessionProvider = ({
  client,
  imageUploader = sameOriginImageUploader,
  children,
}: PropsWithChildren<{
  readonly client: ApiClient;
  readonly imageUploader?: ImageUploader | undefined;
}>): ReactElement => {
  const session = useSession(client);
  const refresh = session.refresh;
  const handleFailure = useCallback(
    (failure: ApiFailure): void => {
      if (failure.kind === 'http' && failure.status === 401) void refresh();
    },
    [refresh],
  );
  const value = useMemo(
    () => ({ ...session, client, imageUploader, handleFailure }),
    [client, handleFailure, imageUploader, session],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSessionContext = (): SessionContextValue => {
  const value = useContext(SessionContext);
  if (value === null) throw new Error('useSessionContext must be used inside SessionProvider.');
  return value;
};
